import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table2, Download, Plus, Trash2 } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import useByggherreAccess from "@/hooks/useByggherreAccess";
import ReadOnlyNotice from "@/components/BasProcess/ReadOnlyNotice";
import { DUTY_ASSIGNMENT_TEMPLATE, DUTY_ROLE_CODES } from "@/lib/basProcess/byggherreDocs";
import { generateDutyAssignmentWordDocument } from "@/lib/documentGenerator/byggherreWordGenerators";

interface Row {
  id: string;
  task: string;
  representative: string;
  handler: string;
  basp: string;
  basu: string;
  note: string | null;
  sort_order: number;
}

const ROLE_COLUMNS: { key: keyof Pick<Row, "representative" | "handler" | "basp" | "basu">; label: string }[] = [
  { key: "representative", label: "Representant" },
  { key: "handler", label: "Handläggare" },
  { key: "basp", label: "Bas-P" },
  { key: "basu", label: "Bas-U" },
];

export const DutyAssignment: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [projectName, setProjectName] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const { canManage } = useByggherreAccess(projectId);

  const load = async () => {
    const [{ data, error }, { data: proj }] = await Promise.all([
      supabase
        .from("duty_assignments")
        .select("*")
        .eq("project_id", projectId)
        .order("sort_order", { ascending: true }),
      supabase.from("projects").select("name").eq("id", projectId).maybeSingle(),
    ]);
    if (error) toast.error("Kunde inte läsa fördelningen");
    setProjectName(proj?.name ?? "");
    setRows((data as unknown as Row[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const seed = async () => {
    setBusy(true);
    const payload = DUTY_ASSIGNMENT_TEMPLATE.map((t, i) => ({
      project_id: projectId,
      task: t.task,
      representative: t.representative,
      handler: t.handler,
      basp: t.basp,
      basu: t.basu,
      sort_order: i,
    }));
    const { error } = await supabase.from("duty_assignments").insert(payload);
    setBusy(false);
    if (error) return toast.error("Kunde inte skapa matrisen");
    toast.success("Matrisen skapad med standarduppgifter");
    load();
  };

  const addRow = async () => {
    const { error } = await supabase.from("duty_assignments").insert({
      project_id: projectId,
      task: "Ny arbetsuppgift",
      sort_order: rows.length + 1,
    });
    if (error) return toast.error("Kunde inte lägga till raden");
    load();
  };

  const update = async (id: string, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const { error } = await supabase.from("duty_assignments").update(patch).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("duty_assignments").delete().eq("id", id);
    if (error) return toast.error("Kunde inte ta bort raden");
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const exportWord = async () => {
    try {
      await generateDutyAssignmentWordDocument(rows, projectName);
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  if (loading)
    return <div className="py-12 text-center text-muted-foreground">Laddar fördelningen...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="54"
        exportHistory={{ documentType: "uppgiftsfordelning", projectId }}
        audit={{ documentType: "uppgiftsfordelning", projectId, title: "Ändringslogg – uppgiftsfördelning" }}
        title="Fördelning av arbetsmiljöuppgifter"
        description="Visar vem som är ansvarig (A), vem som handlägger (HL) och vem som informeras (I) för varje arbetsmiljöuppgift. Särskilt användbar i underhålls- och avhjälpandeprojekt."
        legalRef="AML 3 kap. 6 § · AFS 2023:3 kap. 2"
        icon={Table2}
        actions={
          rows.length ? (
            <Button size="sm" variant="outline" onClick={exportWord}>
              <Download className="mr-1 h-4 w-4" /> Word
            </Button>
          ) : null
        }
      />

      {!canManage && <ReadOnlyNotice />}

      {!rows.length ? (
        <Card>
          <CardContent className="space-y-3 py-10 text-center">
            <Table2 className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mx-auto max-w-md text-sm text-muted-foreground">
              Ingen fördelning upprättad. Skapa matrisen med standarduppgifterna och justera koderna
              efter projektets organisation.
            </p>
            <Button onClick={seed} disabled={busy || !canManage}>
              {busy ? "Skapar..." : "Skapa fördelning"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <p className="text-xs text-muted-foreground">
              {Object.values(DUTY_ROLE_CODES).join(" · ")}
            </p>
            <fieldset disabled={!canManage} className="space-y-3 disabled:opacity-90">
            {rows.map((row) => (
              <div key={row.id} className="space-y-2 rounded-lg border p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <Input
                    key={`t-${row.id}`}
                    className="max-w-xl font-medium"
                    defaultValue={row.task}
                    onBlur={(e) => update(row.id, { task: e.target.value })}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Ta bort raden"
                    onClick={() => remove(row.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid gap-2 sm:grid-cols-4">
                  {ROLE_COLUMNS.map((col) => (
                    <div key={col.key} className="space-y-1">
                      <label className="text-xs text-muted-foreground">{col.label}</label>
                      <Select
                        value={row[col.key]}
                        onValueChange={(v) => update(row.id, { [col.key]: v } as Partial<Row>)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(DUTY_ROLE_CODES).map(([k, label]) => (
                            <SelectItem key={k} value={k}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                <Input
                  key={`n-${row.id}`}
                  placeholder="Kommentar"
                  defaultValue={row.note ?? ""}
                  onBlur={(e) => update(row.id, { note: e.target.value })}
                />
              </div>
            ))}
            <Button size="sm" variant="ghost" onClick={addRow}>
              <Plus className="mr-1 h-4 w-4" /> Lägg till arbetsuppgift
            </Button>
            </fieldset>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DutyAssignment;
